import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowForwardRounded,
  AttachFileRounded,
  CloudUploadOutlined,
  DeleteOutlineRounded,
  InsertDriveFileOutlined,
} from '@mui/icons-material';
import { ref, uploadBytes } from 'firebase/storage';
import {
  CRITERION_STATUS_CODES,
  TMAI_MEMBERSHIP_FRAMEWORK_2022_03,
  getMembershipStageCriteria,
  type CriterionStatus,
  type CriterionStatusCode,
  type DerivedIndicatorKey,
  type MembershipCriterion,
  type MembershipEvidence,
  type MembershipReadinessReport,
  type ReadinessOutcome,
} from '@dosfilos/domain';
import { storage } from '../../lib/firebase';
import { membershipApi } from '../../lib/api/membership.api';
import { useTenantStore } from '../../stores/tenant.store';
import { usePermissions } from '../../hooks/use-permissions';
import {
  useCriterionStatuses,
  useMembershipEvidence,
  useMembershipMutations,
  useMembershipProfile,
  useMembershipReadiness,
} from '../../hooks/use-membership';
import { CapPageHeader, CAP_CONTENT_MAX_WIDTH } from './components/CapPageHeader';
import { errorText } from '../../lib/error-text';

// ─── Institutional criteria (Camino TMAI, R0) ────────────────────────────────
// Every criterion for the target stage, grouped by the document category header.
// Derived criteria show their origin ("de tus datos: 18 de 22") and deep-link to
// the module that feeds them — they are never hand-editable. Manual criteria get
// a status control + evidence uploads.

type ReportCriterion = MembershipReadinessReport['criteria'][number];

// Indicators whose origin is a boolean (no numerator/denominator detail).
const BOOLEAN_INDICATORS: DerivedIndicatorKey[] = [
  'BSC_COHORT_GRADUATED',
  'CAP_BSC_STATUS',
  'CAP_DEP_STATUS',
];

// Where each derived indicator's data lives — deep-link the coordinator to the
// module that fills it.
// Routes reconciled with App.tsx: /people, /academic, /plantillas-syllabus
// (course templates + CCD) exist; /lms, /personas, /academico do not.
const INDICATOR_LINK: Record<DerivedIndicatorKey, { route: string; labelKey: string }> = {
  CCD_COVERAGE: { route: '/plantillas-syllabus', labelKey: 'criteria.link.courses' },
  PROGRAM_CURRICULUM_DOCUMENTED: { route: '/academic', labelKey: 'criteria.link.programs' },
  PROGRAM_MISSION_DEFINED: { route: '/academic', labelKey: 'criteria.link.programs' },
  PROGRAM_DESCRIPTION_DOCUMENTED: { route: '/academic', labelKey: 'criteria.link.programs' },
  FACULTY_ROSTER_SIZE: { route: '/people', labelKey: 'criteria.link.people' },
  ENROLLED_STUDENT_COUNT: { route: '/people', labelKey: 'criteria.link.people' },
  GRADUATE_COUNT: { route: '/people', labelKey: 'criteria.link.people' },
  DOCTRINAL_ATTESTATION_RATE: {
    route: '/cap/institucional/atestaciones',
    labelKey: 'criteria.link.attestations',
  },
  CAP_BSC_STATUS: { route: '/cap', labelKey: 'criteria.link.cap' },
  CAP_DEP_STATUS: { route: '/cap', labelKey: 'criteria.link.cap' },
  BSC_COHORT_GRADUATED: { route: '/cap', labelKey: 'criteria.link.cap' },
};

const OUTCOME_COLOR: Record<
  ReadinessOutcome,
  'success' | 'error' | 'warning' | 'info' | 'default'
> = {
  met: 'success',
  not_met: 'error',
  partial: 'warning',
  no_data: 'info', // never red — "cannot evaluate" is not "does not comply"
  not_applicable: 'default',
};

export function InstitutionalCriteriaPage(): JSX.Element {
  const { t, i18n } = useTranslation('membership');
  const navigate = useNavigate();
  const tenantId = useTenantStore((s) => s.currentTenantId);
  const { isAdmin, hasRole, canAccess } = usePermissions();

  const profileQ = useMembershipProfile();
  const readinessQ = useMembershipReadiness();
  const statusesQ = useCriterionStatuses();
  const evidenceQ = useMembershipEvidence();
  const { runReadiness } = useMembershipMutations();

  const [toast, setToast] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);

  const recalc = async () => {
    if (!tenantId) return;
    setRunError(null);
    try {
      await runReadiness.mutateAsync({ tenantId });
    } catch (e) {
      setRunError(errorText(e));
    }
  };

  // Staff-only page (statuses/evidence callables require admin/registrar); a
  // faculty member's Camino TMAI surface is the doctrinal self-sign page.
  if (!isAdmin && !hasRole('registrar')) {
    return <Navigate to="/cap/institucional/atestaciones" replace />;
  }

  // Wait for the persisted statuses/evidence too: CriterionCard seeds its
  // controls with useState AT MOUNT — rendering the cards before these queries
  // resolve seeds them empty and they never re-seed (the "loads blank on first
  // visit, fine on the second" bug).
  if (profileQ.isLoading || statusesQ.isLoading || evidenceQ.isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }
  if (profileQ.error || !profileQ.data) {
    return (
      <Box sx={{ px: 3, py: 3, maxWidth: CAP_CONTENT_MAX_WIDTH, mx: 'auto' }}>
        <Alert severity="error">{t('board.loadError')}</Alert>
      </Box>
    );
  }

  // Sin objetivo fijado no hay criterios que mostrar: los criterios son los DEL
  // escalón al que el seminario apunta. Se cae a la etapa alcanzada para que la
  // pantalla muestre lo que ya cumplió en vez de quedar en blanco.
  const targetStage = profileQ.data.targetStage ?? profileQ.data.currentStage;
  const criteria = getMembershipStageCriteria(TMAI_MEMBERSHIP_FRAMEWORK_2022_03, targetStage);
  const readiness = readinessQ.data ?? null;
  const reportByCriterion = new Map<string, ReportCriterion>(
    (readiness?.criteria ?? []).map((c) => [c.criterionId, c]),
  );
  // Persisted manual status + evidence per criterion — the controls seed from
  // THESE, so a saved status/note/file shows without re-running the analysis.
  const statusByCriterion = new Map<string, CriterionStatus>(
    (statusesQ.data ?? []).map((s) => [s.criterionId, s]),
  );
  const evidenceByCriterion = new Map<string, MembershipEvidence[]>();
  for (const e of evidenceQ.data ?? []) {
    const list = evidenceByCriterion.get(e.criterionId) ?? [];
    list.push(e);
    evidenceByCriterion.set(e.criterionId, list);
  }

  // Categories that actually have criteria for this stage, in framework order.
  const categoriesInOrder = criteria.reduce<string[]>((acc, c) => {
    if (!acc.includes(c.category)) acc.push(c.category);
    return acc;
  }, []);

  return (
    <Box>
      <CapPageHeader
        backLabel={t(['criteria.back', 'actions.close'])}
        onBack={() => navigate('/cap/institucional')}
        title={t('criteria.title', { stage: t(`stages.${targetStage}`) })}
        subtitle={t('criteria.subtitle')}
      />

      <Box sx={{ px: 3, py: 3, pb: 6, maxWidth: CAP_CONTENT_MAX_WIDTH, mx: 'auto' }}>
        {runError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRunError(null)}>
            {runError}
          </Alert>
        )}

        {!readiness && (
          <Alert
            severity="info"
            sx={{ mb: 3 }}
            action={
              <Button
                color="inherit"
                size="small"
                disabled={runReadiness.isPending}
                onClick={() => void recalc()}
              >
                {t('board.runFirst')}
              </Button>
            }
          >
            {t('criteria.runFirst')}
          </Alert>
        )}

        {criteria.length === 0 ? (
          <Alert severity="info">{t('criteria.empty')}</Alert>
        ) : (
          <Stack spacing={4}>
            {categoriesInOrder.map((category) => (
              <Box key={category}>
                <Typography variant="h6" fontWeight={700} mb={1.5}>
                  {t(`categories.${category}`)}
                </Typography>
                <Stack spacing={2}>
                  {criteria
                    .filter((c) => c.category === category)
                    .map((criterion) => (
                      <CriterionCard
                        key={criterion.id}
                        criterion={criterion}
                        report={reportByCriterion.get(criterion.id)}
                        persisted={statusByCriterion.get(criterion.id)}
                        evidence={evidenceByCriterion.get(criterion.id) ?? []}
                        lang={i18n.language === 'en' ? 'en' : 'es'}
                        onToast={setToast}
                        onNavigate={navigate}
                        canNavigate={canAccess}
                      />
                    ))}
                </Stack>
              </Box>
            ))}
          </Stack>
        )}
      </Box>

      <Snackbar
        open={toast !== null}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
      />
    </Box>
  );
}

// ── One criterion row ──────────────────────────────────────────────────────
function CriterionCard({
  criterion,
  report,
  persisted,
  evidence,
  lang,
  onToast,
  onNavigate,
  canNavigate,
}: {
  criterion: MembershipCriterion;
  report: ReportCriterion | undefined;
  persisted: CriterionStatus | undefined;
  evidence: MembershipEvidence[];
  lang: 'en' | 'es';
  onToast: (msg: string) => void;
  onNavigate: (route: string) => void;
  /** ¿Puede este usuario abrir esa ruta? Un enlace a una pantalla que la guarda
   *  va a rechazar —p. ej. `/people` para un docente (S0·SEC-1b)— no se ofrece. */
  canNavigate: (route: string) => boolean;
}): JSX.Element {
  const { t } = useTranslation('membership');
  const { tenantId, setCriterionStatus, upsertEvidence, deleteEvidence } = useMembershipMutations();

  // Seed the controls from the PERSISTED manual status, not the readiness
  // snapshot — so a saved status/note shows immediately without re-running.
  const [status, setStatus] = useState<CriterionStatusCode | ''>(persisted?.status ?? '');
  const [note, setNote] = useState(persisted?.note ?? '');
  const [noteError, setNoteError] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const isDerived = criterion.evidenceKind === 'derived';
  const label = criterion.label[lang];
  // Chip reflects the readiness snapshot if present, else the persisted status.
  const chipStatus: ReadinessOutcome | undefined = report?.status ?? persisted?.status;
  const accent = chipStatus ? `${OUTCOME_COLOR[chipStatus]}.main` : 'divider';

  // Persist status + the current note together. Reused by the select and the
  // note's own save button so a note typed AFTER choosing a status is not lost.
  const persist = async (next: CriterionStatusCode) => {
    if (next === 'not_applicable' && !note.trim()) {
      setNoteError(true);
      return;
    }
    setNoteError(false);
    setSaveError(null);
    try {
      await setCriterionStatus.mutateAsync({
        tenantId,
        criterionId: criterion.id,
        status: next,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onToast(t('criteria.status.saved'));
    } catch (e) {
      setSaveError(errorText(e));
    }
  };

  const downloadEvidence = async (id: string) => {
    setDownloadingId(id);
    setSaveError(null);
    try {
      const { url } = await membershipApi.evidenceDownloadUrl({ tenantId, evidenceId: id });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setSaveError(errorText(e));
    } finally {
      setDownloadingId(null);
    }
  };

  const confirmDelete = async () => {
    if (!confirmDeleteId) return;
    setSaveError(null);
    try {
      await deleteEvidence.mutateAsync({ tenantId, evidenceId: confirmDeleteId });
      onToast(t('criteria.evidence.deleted'));
    } catch (e) {
      setSaveError(errorText(e));
    } finally {
      setConfirmDeleteId(null);
    }
  };

  // Origin line for a derived criterion.
  const originText = (): string | null => {
    if (!isDerived || !report?.derived || !criterion.derivedIndicator) return null;
    const ind = criterion.derivedIndicator;
    if (BOOLEAN_INDICATORS.includes(ind)) {
      return t(`criteria.origin.${ind}_${report.derived.satisfied ? 'yes' : 'no'}`);
    }
    const detail = report.derived.detail;
    return t(`criteria.origin.${ind}`, {
      numerator: detail?.numerator ?? 0,
      denominator: detail?.denominator ?? 0,
    });
  };

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, borderRadius: 3, borderLeft: '4px solid', borderLeftColor: accent }}
    >
      <Stack direction="row" alignItems="flex-start" spacing={1.5} flexWrap="wrap" mb={1}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1, minWidth: 200 }}>
          {label}
        </Typography>
        {report && (
          <Chip
            size="small"
            color={OUTCOME_COLOR[report.status]}
            // `no_data` es UN estado del dominio con DOS causas, y el texto las
            // separa acá, en el render: al derivado le faltan datos de origen, al
            // manual le falta la respuesta del TC. El dominio emite el código, la
            // capa de presentación elige la palabra — no se meten dos estados en
            // el dominio para resolver un problema de redacción.
            label={t(
              report.status === 'no_data'
                ? `criteria.outcome.no_data_${isDerived ? 'derived' : 'manual'}`
                : `criteria.outcome.${report.status}`,
            )}
          />
        )}
      </Stack>

      {/* No-data suggested action (never red). */}
      {report?.status === 'no_data' && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          {t([
            `criteria.noDataAction.${criterion.derivedIndicator ?? 'default'}`,
            'criteria.noDataAction.default',
          ])}
        </Typography>
      )}

      {/* Derived → origin + deep link, not editable. */}
      {isDerived && report?.derived && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            <Box component="span" fontWeight={600}>
              {t('criteria.originPrefix')}{' '}
            </Box>
            {originText()}
          </Typography>
          {criterion.derivedIndicator &&
            canNavigate(INDICATOR_LINK[criterion.derivedIndicator].route) && (
              <Button
                size="small"
                endIcon={<ArrowForwardRounded />}
                sx={{ textTransform: 'none', mt: 0.5, px: 0 }}
                onClick={() => onNavigate(INDICATOR_LINK[criterion.derivedIndicator!].route)}
              >
                {t(INDICATOR_LINK[criterion.derivedIndicator].labelKey)}
              </Button>
            )}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            {t('criteria.derivedNotEditable')}
          </Typography>
        </Box>
      )}

      {/* Non-derived → editable status + evidence. */}
      {!isDerived && (
        <Stack spacing={1.5} mt={1}>
          {saveError && (
            <Alert severity="error" onClose={() => setSaveError(null)}>
              {saveError}
            </Alert>
          )}
          <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap">
            <TextField
              select
              size="small"
              label={t('criteria.status.edit')}
              value={status}
              onChange={(e) => {
                const next = e.target.value as CriterionStatusCode;
                setStatus(next);
                void persist(next);
              }}
              sx={{ minWidth: 180 }}
              disabled={setCriterionStatus.isPending}
            >
              {CRITERION_STATUS_CODES.map((code) => (
                <MenuItem key={code} value={code}>
                  {t(`criteria.outcome.${code}`)}
                </MenuItem>
              ))}
            </TextField>
            <Button
              size="small"
              startIcon={<AttachFileRounded />}
              sx={{ textTransform: 'none', mt: 0.5 }}
              onClick={() => setEvidenceOpen(true)}
            >
              {t('criteria.evidence.attach')}
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="flex-start">
            <TextField
              size="small"
              fullWidth
              label={t('criteria.status.note')}
              value={note}
              onChange={(e) => {
                setNote(e.target.value);
                if (e.target.value.trim()) setNoteError(false);
              }}
              error={noteError}
              helperText={noteError ? t('criteria.status.noteRequired') : undefined}
              disabled={setCriterionStatus.isPending}
            />
            {/* A note needs a status to persist (the status doc carries it). */}
            <Button
              size="small"
              variant="outlined"
              sx={{ textTransform: 'none', mt: 0.5, flexShrink: 0 }}
              disabled={setCriterionStatus.isPending || !status || note === (persisted?.note ?? '')}
              onClick={() => status && void persist(status)}
            >
              {t('criteria.status.saveNote')}
            </Button>
          </Stack>

          {/* Persisted evidence for this criterion — download via signed URL. */}
          {evidence.length > 0 && (
            <Stack spacing={0.5}>
              {evidence.map((ev) => (
                <Stack key={ev.id} direction="row" spacing={0.5} alignItems="center">
                  <InsertDriveFileOutlined fontSize="small" sx={{ color: 'text.secondary' }} />
                  <Button
                    size="small"
                    variant="text"
                    sx={{
                      textTransform: 'none',
                      px: 0,
                      minWidth: 0,
                      flex: 1,
                      justifyContent: 'flex-start',
                    }}
                    disabled={downloadingId === ev.id}
                    onClick={() => void downloadEvidence(ev.id)}
                  >
                    {downloadingId === ev.id ? (
                      <CircularProgress size={14} />
                    ) : (
                      (ev.label ?? t('criteria.evidence.file'))
                    )}
                  </Button>
                  <IconButton
                    size="small"
                    aria-label={t('criteria.evidence.delete')}
                    onClick={() => setConfirmDeleteId(ev.id)}
                    sx={{ color: 'text.secondary' }}
                  >
                    <DeleteOutlineRounded fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
        </Stack>
      )}

      <Dialog
        open={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t('criteria.evidence.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t('criteria.evidence.deleteConfirm')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDeleteId(null)} disabled={deleteEvidence.isPending}>
            {t('actions.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={deleteEvidence.isPending ? <CircularProgress size={16} /> : undefined}
            disabled={deleteEvidence.isPending}
            onClick={() => void confirmDelete()}
          >
            {t('criteria.evidence.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <EvidenceUploadDialog
        open={evidenceOpen}
        onClose={() => setEvidenceOpen(false)}
        tenantId={tenantId}
        criterionId={criterion.id}
        onUploaded={() => {
          setEvidenceOpen(false);
          onToast(t('criteria.evidence.uploaded'));
        }}
        upload={upsertEvidence.mutateAsync}
      />
    </Paper>
  );
}

// ── Evidence upload dialog (mold: SubmitAdmissionDocumentsDialog) ───────────
function EvidenceUploadDialog({
  open,
  onClose,
  tenantId,
  criterionId,
  onUploaded,
  upload,
}: {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  criterionId: string;
  onUploaded: () => void;
  upload: (v: {
    tenantId: string;
    criterionId: string;
    kind: 'upload';
    storageRef: string;
    label: string;
  }) => Promise<unknown>;
}): JSX.Element {
  const { t } = useTranslation('membership');
  const [file, setFile] = useState<File | null>(null);
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onDrop = useCallback((accepted: File[]) => {
    const f = accepted[0];
    if (f) setFile(f);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
    },
  });

  const reset = () => {
    setFile(null);
    setLabel('');
    setError(null);
  };
  const handleClose = () => {
    if (busy) return;
    reset();
    onClose();
  };

  const handleUpload = async () => {
    if (!file || !tenantId) return;
    setBusy(true);
    setError(null);
    try {
      const path = `tenants/${tenantId}/membershipEvidence/${criterionId}/${Date.now()}_${file.name}`;
      await uploadBytes(ref(storage, path), file);
      await upload({
        tenantId,
        criterionId,
        kind: 'upload',
        storageRef: path,
        label: label.trim() || file.name,
      });
      reset();
      onUploaded();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('criteria.evidence.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <Box
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'divider',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'action.hover' : 'background.default',
              transition: 'all 0.2s',
              '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.light' },
            }}
          >
            <input {...getInputProps()} />
            {file ? (
              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                <InsertDriveFileOutlined sx={{ color: 'text.secondary' }} />
                <Typography variant="body2" fontWeight={600}>
                  {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                </Typography>
              </Stack>
            ) : (
              <>
                <CloudUploadOutlined sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('criteria.evidence.attach')}
                </Typography>
              </>
            )}
          </Box>
          <TextField
            size="small"
            fullWidth
            label={t('criteria.evidence.labelPlaceholder')}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            disabled={busy}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={busy}>
          {t('actions.cancel')}
        </Button>
        <Button
          variant="contained"
          startIcon={busy ? <CircularProgress size={16} /> : undefined}
          disabled={busy || !file}
          onClick={() => void handleUpload()}
        >
          {busy ? t('criteria.evidence.uploading') : t('criteria.evidence.attach')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
